import type { CollapseProps } from 'antd'
import { useState, useEffect, useMemo, useContext } from 'react'
import { Form, Collapse, Row, Col, ConfigProvider, Radio, Input, Button, Timeline, message } from 'antd'
import { CloseCircleFilled } from '@ant-design/icons'
import { EasyIcon, EasyImg, ModalContext, Scrollbars } from 'src/components'
import { mainStore } from 'enasops/stores'
import { $api, $consts } from 'src/plugins'
import { getImgBase } from 'src/utils/axios'
import { extendRules, getSingleRule, initFilter } from 'src/utils/common'
import { appStore } from 'src/stores'

const AuditCheck = (props: { id: string }) => {
  const modal = useContext(ModalContext)

  const [form] = Form.useForm()
  const [detail, setDetail] = useState<any>({})
  const [auditList, setAuditList] = useState<any[]>([])

  useEffect(() => {
    const payload = initFilter({ OrgId: mainStore.headers.OrgId })
    extendRules(payload, [getSingleRule('ID', 1, props.id)])

    const audit = initFilter({ OrgId: mainStore.headers.OrgId, PageIndex: 1, Limit: 1000, Sort: 'CREATE_TIME', Order: 1 })
    extendRules(audit, [getSingleRule('TENANT_ID', 1, props.id)])

    Promise.all([
      $api['app/getDataByPost'](payload, {
        url: '/OP/Tenant/GetOne'
      }),
      $api['app/getDataByPost'](audit, {
        url: '/OP/Tenant/GetRecordOrderPaged'
      }),
    ]).then(datas => {
      const [detail, auditList] = datas
      if (detail) {
        form.setFieldsValue({
          FLOW_STATUS: detail.FLOW_STATUS,
          REMARK: detail.REMARK
        })
        setDetail(detail)
      }
      auditList && setAuditList(auditList)
    })
  }, [props.id])

  const handleAudit = (values: any) => {
    $api['app/getDataByPost'](
      { ...detail, ...values },
      { url: '/OP/Tenant/UpdateFlowStatus' }
    ).then(res => {
      if (res) {
        message.success('操作成功')
        handleCancel()
      }
    })
  }

  const handleCancel = () => {
    modal.close()
  }

  const [flowTypes, setFlowTypes] = useState<{ label: string, value: number }[]>([])
  useEffect(() => {
    appStore.getEnums([$consts['ENUMS/FLOW_STATUS']]).then(res => {
      res && setFlowTypes(appStore.getEnumOptions($consts['ENUMS/FLOW_STATUS']))
    })
  }, [])

  const items = useMemo<CollapseProps['items']>(() => {
    const logos = JSON.parse(detail.LOGO || '[]')
    const logoSrc = logos.length ? `${mainStore.baseConfig.FileDomain || getImgBase()}${logos[0].FilePath}` : ''
    const bgs = JSON.parse(detail.LOGIN_BACKGROUND_IMAGE || '[]')
    const bgSrc = bgs.length ? `${mainStore.baseConfig.FileDomain || getImgBase()}${bgs[0].FilePath}` : ''
    return [
      {
        key: 'baseInfo',
        label: <span className='op003__title'>基本信息</span>,
        children: (
          <Row>
            <Col span={12}>
              <div className='op003__item'><div>企业名称/租户名称</div><div>{detail.NAME || '--'}</div></div>
              <div className='op003__item'><div>行业类型</div><div>{detail.IndustryTypeName || '--'}</div></div>
              <div className='op003__item'><div>所在省市</div><div>{detail.REGIONAL || '--'}</div></div>
              <div className='op003__item'><div>注册联系人</div><div>{detail.CONTACTS || '--'}</div></div>
              <div className='op003__item'><div>注册联系人邮箱</div><div>{detail.EMAIL || '--'}</div></div>
            </Col>
            <Col span={12}>
              <div className='op003__item'><div>企业简称/租户简称</div><div>{detail.REFERRED || '--'}</div></div>
              <div className='op003__item'><div>统一社会信用代码</div><div>{detail.UNIFIED_SOCIAL_CREDIT_CODE || '--'}</div></div>
              <div className='op003__item'><div>详细地址</div><div>{detail.ADDRESS || '--'}</div></div>
              <div className='op003__item'><div>注册联系人手机</div><div>{detail.PHONE || '--'}</div></div>
              <div className='op003__item'><div></div><div></div></div>
            </Col>
          </Row>
        ),
      },
      {
        key: 'detailInfo',
        label: <span className='op003__title'>详细信息</span>,
        children: (
          <Row>
            <Col span={12}>
              <div className='op003__item'><div>管理人员</div><div>{detail.MANAGER || '--'}</div></div>
              <div className='op003__item'><div>管理人员邮箱</div><div>{detail.MANAGER_EMAIL || '--'}</div></div>
              <div className='op003__item'><div>公司简介</div><div>{detail.COMPANY_PROFILE || '--'}</div></div>
            </Col>
            <Col span={12}>
              <div className='op003__item'><div>管理人员手机</div><div>{detail.MANAGER_PHONE || '--'}</div></div>
              <div className='op003__item'><div>公司传真</div><div>{detail.COMPANY_FAX || '--'}</div></div>
              <div className='op003__item'><div>公司网址</div><div>{detail.COMPANY_WEBSITE || '--'}</div></div>
            </Col>
          </Row>
        ),
      },
      {
        key: 'customInfo',
        label: <span className='op003__title'>自定义信息</span>,
        children: (
          <Row>
            <Col span={12}>
              <div className='op003__item'>
                <div>公司logo</div>
                <div>{logoSrc ? <EasyImg src={logoSrc} className='op003__img' /> : '--'}</div>
              </div>
              <div className='op003__item'><div>自定义页面标题</div><div>{detail.PAGE_TITLE || '--'}</div></div>
            </Col>
            <Col span={12}>
              <div className='op003__item'>
                <div>登录页自定义背景图</div>
                <div>{bgSrc ? <EasyImg src={bgSrc} className='op003__img' /> : '--'}</div>
              </div>
              <div className='op003__item'><div></div><div></div></div>
            </Col>
          </Row>
        ),
      },
    ]
  }, [detail])

  return (
    <div className='op003'>
      <ConfigProvider theme={{ components: { Collapse: { headerBg: '#5d6787', contentPadding: 0 } } }}>
        <Collapse
          expandIcon={() => <EasyIcon type='DownOutlined' style={{ color: 'white' }} />}
          items={items}
          defaultActiveKey={['baseInfo', 'detailInfo', 'customInfo']}
        />
      </ConfigProvider>
      <div className='op003__footer'>
        <Row gutter={0}>
          <Col span={12} className='pd-r8'>
            <Form form={form} layout='horizontal' onFinish={handleAudit}>
              <Form.Item name='FLOW_STATUS' label='审核结果'>
                <Radio.Group options={flowTypes} />
              </Form.Item>
              <Form.Item label='未通过原因' />
              <Form.Item name='REMARK'>
                <Input.TextArea rows={5} />
              </Form.Item>
            </Form>
          </Col>
          <Col span={12}>
            <Scrollbars autoHide autoHeight autoHeightMax={216}>
              <Form layout='horizontal' className='pd-l8 pd-r8'>
                <Form.Item label='审核记录' />
                <Form.Item>
                  <Timeline
                    pending
                    pendingDot={<div />}
                    items={auditList.map((item: any, index: number) => {
                      return {
                        dot: <CloseCircleFilled style={{ fontSize: 20, color: '#FF5733' }} />,
                        children: (
                          <Form layout='horizontal' className={`op003__footer-audit ${index === 0 ? 'active' : ''}`}>
                            <Form.Item label='时间'>{item.CREATE_TIME}</Form.Item>
                            <Form.Item label='人员'>{item.USER_NAME}</Form.Item>
                            <Form.Item label='原因'>{item.REASON}</Form.Item>
                          </Form>
                        )
                      }
                    })}
                    className='op003__footer-timeline'
                  />
                </Form.Item>
              </Form>
            </Scrollbars>
          </Col>
        </Row>
        <div className='op003__footer-submit'>
          <Button onClick={handleCancel} className='mg-r8'>取消</Button>
          <Button type='primary' onClick={() => form.submit()}>审核完成</Button>
        </div>
      </div>
    </div>
  )
}

export default AuditCheck